import type { StorefrontExternalBookingDto } from "@tripcart/shared";
import {
  Button,
  type ButtonSize,
} from "@tripcart/traveler-ui/components/button";
import { ExternalLink } from "lucide-react";

import { useTranslator } from "@/lib/translation-context";

import styles from "./external-book-button.module.css";
import { ExternalBookingHtml } from "./external-booking-html";

interface ExternalBookButtonProps {
  booking: StorefrontExternalBookingDto;
  size?: ButtonSize;
  className?: string;
  /** Points at the note under the rail's button that says where it opens. */
  describedBy?: string;
}

/**
 * The Book button of a tour that books on the agency's own site (External
 * Booking Link). "link" mode is a plain link to the agency's booking page,
 * rendered on the server with no handler. "html" mode is the operator's own
 * widget HTML, in this button's place.
 */
export function ExternalBookButton({
  booking,
  size = "lg",
  className,
  describedBy,
}: ExternalBookButtonProps) {
  const t = useTranslator();
  if (booking.type === "html") {
    return <ExternalBookingHtml html={booking.html} className={className} />;
  }
  const newTab = booking.openInNewTab;
  return (
    <Button
      variant="book"
      size={size}
      className={className}
      render={
        <a
          href={booking.url}
          target={newTab ? "_blank" : undefined}
          rel={newTab ? "noopener noreferrer" : "noopener"}
          aria-describedby={describedBy}
        />
      }
    >
      {/* Wrapped so a long operator-set label truncates with an ellipsis
          instead of overflowing the button when `.ctaShrinkable` shrinks it
          — see `.label`. The label is the operator's own words, so it is not
          translated. An empty label is allowed by the saved schema; the
          default stands in. */}
      <span className={styles.label}>
        {booking.buttonLabel || t("Book now")}
      </span>
      <ExternalLink aria-hidden strokeWidth={1.75} className={styles.icon} />
    </Button>
  );
}
